import { PlateformeDto } from 'api/plateforme';
import { EntityDetailsHeader } from 'components/display/EntityDetailsHeader';
import { GenericLoading } from 'components/ui/Loader';
import { Sizes } from 'enums/sizes';
import { usePanel } from 'hooks/usePanel';
import {
  Building2,
  Filter,
  KeyRound,
  Package,
  UserCog,
  Users
} from 'lucide-react';
import { FC, useCallback, useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { plateformesAPI } from 'services/swagger-api';
import { BrokerFormValues } from '../brokers-view/form/BrokerFormValues';
import { useBrokerForm } from '../brokers-view/hooks/useBrokerForm';
import { LogosSection } from './LogosSection';
import { PlateformeBrokerLinker } from './PlateformeBrokerLinker';
import { ThemeMosaicHeaderBadge } from './ThemeMosaicHeaderBadge';
import { getCorporate } from './api/corporateApi';
import { estAdministree as estModeAdministree } from './modeGestion';
import { PlateformeOutletContext } from './detail/plateformeTabRoutes';
import { SideNavMenu } from 'back-office/shared/components/SideNavMenu';

/**
 * Fiche plateforme : header + menu latéral d'onglets déclarés en ROUTES
 * IMBRIQUÉES (/plateformes/{id}/gouvernance, /intermediaires, /catalogue,
 * /roles, /habilitations) — l'URL est la source de vérité de l'onglet actif,
 * donc précédent/suivant et liens profonds tombent sur le bon onglet. Le
 * contenu est rendu par l'Outlet, qui reçoit la plateforme et les actions
 * partagées en contexte.
 */
export const PlateformeDetails: FC = () => {
  const { plateformeId } = useParams<{ plateformeId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [plateforme, setPlateforme] = useState<PlateformeDto>();
  const [corporateName, setCorporateName] = useState<string | null>(null);
  const { createPlateformeBroker } = useBrokerForm();

  const { openPanel, closePanel } = usePanel();

  const fetchPlateformeDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      if (plateformeId) {
        const response = await plateformesAPI.getPlateformeByID({
          plateformeId
        });
        if (response) {
          setPlateforme(response);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [plateformeId]);

  const createBroker = useCallback(
    async (values: BrokerFormValues) => {
      if (plateformeId) {
        const response = await createPlateformeBroker(plateformeId, values);
        if (response.isSuccess) {
          fetchPlateformeDetails();
          return response;
        }
      }
    },
    [plateformeId, createPlateformeBroker, fetchPlateformeDetails]
  );

  const linkBroker = useCallback(
    async (brokerId: string) => {
      if (plateformeId) {
        const response = await plateformesAPI.linkBroker({
          plateformeId,
          brokerId
        });
        if (response.isSuccess) {
          fetchPlateformeDetails();
          closePanel();
        }
      }
    },
    [plateformeId, closePanel, fetchPlateformeDetails]
  );

  const openAddBrokerForm = useCallback(() => {
    openPanel(
      {
        title: 'Ajouter un courtier dans la plateforme',
        size: Sizes['4xl']
      },
      <PlateformeBrokerLinker
        onSubmit={createBroker}
        onLinkBroker={linkBroker}
      />
    );
  }, [openPanel, createBroker, linkBroker]);

  const openLogosPanel = useCallback(() => {
    if (!plateforme) return;
    openPanel(
      {
        title: 'Logos de la plateforme',
        size: Sizes.lg
      },
      <LogosSection plateforme={plateforme} refresh={fetchPlateformeDetails} />
    );
  }, [openPanel, plateforme, fetchPlateformeDetails]);

  // Nom de la PM gérante pour le titre « Plateforme gérée par … » — best-effort.
  useEffect(() => {
    if (!plateformeId) return;
    getCorporate(plateformeId)
      .then(corporate => setCorporateName(corporate?.name ?? null))
      .catch(() => setCorporateName(null));
  }, [plateformeId]);

  useEffect(() => {
    fetchPlateformeDetails();
  }, [fetchPlateformeDetails]);

  if (!plateforme || !plateformeId || isLoading) {
    return <GenericLoading isPageCentered loading />;
  }

  const outletContext: PlateformeOutletContext = {
    plateformeId,
    plateforme,
    refreshPlateforme: fetchPlateformeDetails,
    openAddBrokerForm
  };

  return (
    <section className="w-full overflow-hidden border bg-white rounded-2xl">
      <EntityDetailsHeader
        config={{
          icon: <Building2 className="text-primary" />,
          logoURL: plateforme.theme?.logoUrl,
          onLogoClick: openLogosPanel,
          title: corporateName
            ? estModeAdministree(plateforme.modeGestion)
              ? `Plateforme administrée — ${corporateName}`
              : `Plateforme gérée par ${corporateName}`
            : plateforme.name,
          subtitle: plateforme.code,
          infoSections: [],
          customAside: (
            <div className="flex items-center gap-3">
              {/* PM principale gérée directement dans l'onglet Informations (notion
                unifiée) — plus de badge/lien vers une fiche PM séparée. */}
              <ThemeMosaicHeaderBadge
                plateforme={plateforme}
                refresh={fetchPlateformeDetails}
              />
            </div>
          )
        }}
      />
      <div className="p-8 shadow-inner">
        <div className="flex gap-6">
          <div className="flex-shrink-0 w-56">
            <SideNavMenu
              items={[
                {
                  id: 'gouvernance',
                  to: 'gouvernance',
                  label: 'Gouvernance',
                  icon: UserCog
                },
                {
                  id: 'intermediaires',
                  to: 'intermediaires',
                  label: 'Intermédiaires',
                  icon: Users
                },
                { id: 'roles', to: 'roles', label: 'Rôles', icon: KeyRound },
                {
                  id: 'catalogue',
                  to: 'catalogue',
                  label: 'Catalogue',
                  icon: Package
                },
                {
                  id: 'habilitations',
                  to: 'habilitations',
                  label: 'Habilitations tunnels',
                  icon: Filter
                }
              ]}
            />
          </div>

          <div className="flex-1 min-w-0">
            <Outlet context={outletContext} />
          </div>
        </div>
      </div>
    </section>
  );
};
